// src/components/Support.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Send, CheckCircle2, Loader2, Sparkles, Crown } from 'lucide-react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import { submitSupportContact } from '../utils/supportContact';
import { createCheckoutSession, createBillingPortalSession } from '../utils/billingCheckout';
import { useEntitlementSummary } from '../context/EntitlementSummaryContext';
import {
  shouldShowManageBillingCta,
  shouldShowSubscribeCta,
} from '../utils/enhancementEntitlementHint';

function Support() {
  const [user] = useAuthState(auth);
  const { summary } = useEntitlementSummary();
  const [billingBusy, setBillingBusy] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError(null);
  };

  const handleSubscribe = async () => {
    setBillingBusy(true);
    try {
      const url = await createCheckoutSession();
      window.location.href = url;
    } catch (err) {
      setError(err?.message || 'Could not start checkout.');
      setBillingBusy(false);
    }
  };

  const handleManageBilling = async () => {
    setBillingBusy(true);
    try {
      const url = await createBillingPortalSession();
      window.location.href = url;
    } catch (err) {
      setError(err?.message || 'Could not open billing portal.');
      setBillingBusy(false);
    }
  };

  const showSubscribe = Boolean(user) && shouldShowSubscribeCta(summary);
  const showManage = Boolean(user) && shouldShowManageBillingCta(summary);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await submitSupportContact({
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
        website: formData.website,
      });

      setSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '', website: '' });
      
      // Reset success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      console.error('Support form error:', err);
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 md:p-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Support
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
            Get help with Epiphoric
          </p>

          <div className="prose prose-gray dark:prose-invert max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                How Can We Help?
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-6">
                We're here to help you get the most out of Epiphoric. Whether you have questions, 
                need help with a feature, or want to report a bug, send us a message and we'll get back to you.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
                Contact Us
              </h2>
              
              {success && (
                <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <p className="text-green-700 dark:text-green-300">
                    Message sent successfully! We'll get back to you soon.
                  </p>
                </div>
              )}

              {error && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-red-700 dark:text-red-300">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="website">Website</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Your Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="What can we help you with?"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical"
                    placeholder="Please describe your question, issue, or feedback..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-3 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </section>

            {/* AI enhancement (US-09 — aligned with docs/ENTITLEMENTS.md) */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-purple-500" />
                AI enhancement
              </h2>
              
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-6 mb-6">
                <div className="flex items-start gap-3">
                  <Crown className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" aria-hidden />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      Access and complimentary credit
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 mb-3">
                      <strong>Autopilot</strong> and <strong>Guided Journey</strong> require an active entitlement on your account.
                      Many accounts receive <strong>one complimentary AI enhancement credit</strong>; using Autopilot or completing Guided Journey consumes that credit (you do not get both on the same complimentary credit alone).
                    </p>
                    <p className="text-gray-700 dark:text-gray-300">
                      If you need more access, subscribe for{' '}
                      <strong className="text-purple-600 dark:text-purple-400">$10/month</strong> —{' '}
                      <strong>100 AI enhancement credits</strong> per billing period (Autopilot uses 1 credit;
                      Guided Journey uses up to 3 credits for a full journey). Paid plans renew credits each
                      billing period; unused credits do not roll over.
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 mt-3">
                      Use the buttons below to start checkout or manage your subscription. For other requests,
                      contact us with the subject{' '}
                      <strong className="text-purple-600 dark:text-purple-400">&quot;AI access request&quot;</strong>.
                    </p>
                    {user && (showSubscribe || showManage) && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {showSubscribe && (
                          <button
                            type="button"
                            onClick={handleSubscribe}
                            disabled={billingBusy}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
                          >
                            {billingBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                            Subscribe — $10/month
                          </button>
                        )}
                        {showManage && (
                          <button
                            type="button"
                            onClick={handleManageBilling}
                            disabled={billingBusy}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-purple-300 dark:border-purple-600 text-purple-800 dark:text-purple-200 hover:bg-purple-100/50 dark:hover:bg-purple-900/30 disabled:opacity-50"
                          >
                            Manage subscription
                          </button>
                        )}
                      </div>
                    )}
                    {!user && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
                        <Link to="/auth" className="text-purple-600 dark:text-purple-400 hover:underline font-medium">
                          Sign in
                        </Link>{' '}
                        to subscribe.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    What are the AI features?
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    <strong>Autopilot</strong> automatically develops your idea into a full plan with title,
                    description, tags, tasks, and execution steps. <strong>Guided Journey</strong> lets you
                    collaborate with AI step-by-step, giving you control at each stage.
                  </p>
                </div>

                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    What if enhancement isn&apos;t available?
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Try <strong>refreshing the page</strong> or <strong>signing out and back in</strong> so your account state can update.
                    If you have used your complimentary credit, contact us using the form below. For persistent errors, include what you were doing (Autopilot vs Guided) and approximate time.
                  </p>
                </div>

                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Can I still use Epiphoric without AI?
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Absolutely. Core features (idea management, tags, tasks, resources, encryption) work without AI enhancement.
                    The &quot;Raw Save&quot; option lets you capture ideas quickly without using a credit, and you can develop ideas manually.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                Common Questions
              </h2>
              
              <div className="space-y-4">
                <div className="border-l-4 border-primary pl-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    How do I reset my password?
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Click "Forgot password?" on the login page and enter your email address. 
                    You'll receive a password reset link via email.
                  </p>
                </div>

                <div className="border-l-4 border-primary pl-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Is my data encrypted?
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Yes! All your ideas and sensitive data are encrypted client-side before being 
                    stored. Only you can decrypt and read your data.
                  </p>
                </div>

                <div className="border-l-4 border-primary pl-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Can I export my data?
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Yes, you can export your data at any time. Go to your profile settings to 
                    download your encrypted data.
                  </p>
                </div>

                <div className="border-l-4 border-primary pl-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    How do I delete my account?
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    You can delete your account from your profile settings. This will permanently 
                    delete all your data. Make sure to export your data first if you want to keep a backup.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                Response Time
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                We typically respond to support requests within 24-48 hours. For urgent issues, 
                please include "URGENT" in your subject line.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Support;
